import type {CompRef, DalValue, OverrideOrDefaultData, Pointer} from '@wix/document-services-types'
import {guidUtils} from '@wix/santa-core-utils'
import type {
    ComponentsExtensionAPI,
    DataExtensionAPI,
    DataModelExtensionAPI,
    HooksExtensionApi,
    InnerElementsExtensionAPI,
    ManifestApi,
    ManifestExtensionAPI,
    SchemaExtensionAPI,
    SlotsExtensionAPI,
    StructureExtensionAPI
} from '@wix/document-manager-extensions'
import {populateSlotWithSystemContainer} from './slotPopulationUtils'
import type {CreateExtArgs} from '@wix/document-manager-core'
import {DATA_TYPES} from '../../../constants/constants'
import {getManifestItems} from '../utils'

type PropertyItemMap = Record<string, PropertyItem[]>

interface PropertyItem {
    _id?: string
    data: {}
    type: string
}

const shouldProcessContainerKey = (arrayItemDataManifest: OverrideOrDefaultData, key: string): boolean => {
    const itemManifest = getManifestItems(arrayItemDataManifest.metaData)[key]
    const containerDataType = itemManifest?.dataType === 'container'

    if (!containerDataType || !itemManifest.container) {
        return false
    }

    const {containerType} = itemManifest.container
    return containerDataType && ['simple', 'template', 'placeholder'].includes(containerType)
}

const tryRemoveSystemContainerFromSlot = (
    createExtArgs: CreateExtArgs,
    slotsData: DalValue,
    slotGuid: string
): boolean => {
    const {pointers, extensionAPI} = createExtArgs
    const {components} = extensionAPI as ComponentsExtensionAPI
    //Once we add a validator check to ensure each GUID item exists in the slot as a key, this can be removed
    if (!slotsData?.slots?.[slotGuid]) {
        return false
    }

    const systemContainerId = slotsData.slots[slotGuid]
    const systemContainerPointer = pointers.getPointer(systemContainerId, 'DESKTOP')

    components.removeComponent(systemContainerPointer)

    delete slotsData.slots[slotGuid]

    return true
}

const getDataToUpdate = (
    manifest: ManifestApi,
    componentRef: Pointer,
    includeInnerElements: boolean,
    propertyItemMap: PropertyItemMap
): OverrideOrDefaultData[] => {
    return manifest
        .getData(componentRef, includeInnerElements, [{prop: 'arrayItems'}])
        .filter(({prop}: {prop: string}) => propertyItemMap[prop]?.length)
}

const findDuplicateSource = (
    newItem: PropertyItem,
    existingPropertyItems: PropertyItem[],
    containerKeys: string[]
): PropertyItem | undefined => {
    return existingPropertyItems.find(existingItem => {
        return containerKeys.some(key => {
            return existingItem[key] && newItem[key] && existingItem[key] === newItem[key]
        })
    })
}

const getContainerIdFromSlotObject = (
    createExtArgs: CreateExtArgs,
    innerElementPath: string[] | undefined,
    componentRef: Pointer,
    sourceSlotGuid: any
) => {
    const {extensionAPI} = createExtArgs
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI

    if (innerElementPath) {
        const slotsData = innerElements.getInnerElementSlots(componentRef as CompRef, innerElementPath)
        return slotsData[sourceSlotGuid].id
    }

    const slotsData = slots.getSlotsData(componentRef as CompRef)
    return slotsData[sourceSlotGuid].id
}

const getSlotInfoForComponent = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    innerElementPath: string[] | undefined
) => {
    const {pointers, extensionAPI} = createExtArgs
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const pageId = pointers.structure.getPageOfComponent(componentRef as CompRef).id

    let slotDataItem: DalValue
    let slotsPointer

    if (innerElementPath) {
        slotDataItem = innerElements.getInnerElementSlotItem(componentRef as CompRef, innerElementPath)
        const innerElement = innerElements.getInnerElement(componentRef as CompRef, innerElementPath)
        slotsPointer = pointers.getPointer(innerElement!.slotsQuery, DATA_TYPES.slots)
    } else {
        slotDataItem = slots.getSlotsDataItem(componentRef as CompRef)
        slotsPointer = dataModel.components.getItemPointer(componentRef, 'slots')
    }

    return {pageId, slotDataItem, slotsPointer}
}

const duplicateSystemContainer = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    sourceItem: PropertyItem,
    newItem: PropertyItem,
    containerKey: string,
    arrayItemDataManifest: OverrideOrDefaultData
) => {
    const {extensionAPI, pointers} = createExtArgs
    const {components} = extensionAPI as ComponentsExtensionAPI
    const {hooks} = extensionAPI as HooksExtensionApi
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const innerElementPath = arrayItemDataManifest.path

    const sourceSlotGuid = sourceItem[containerKey]
    const newSlotGuid = guidUtils.getGUID()

    newItem[containerKey] = newSlotGuid

    const sourceContainerId = getContainerIdFromSlotObject(
        createExtArgs,
        innerElementPath,
        componentRef,
        sourceSlotGuid
    )

    if (sourceContainerId) {
        const sourceContainerPointer = pointers.getPointer(sourceContainerId, 'DESKTOP') as CompRef
        const duplicatedContainerRef = components.getComponentToAddRef()

        hooks.executeHook({
            event: 'component_duplicate',
            // @ts-ignore
            compRefToAdd: duplicatedContainerRef,
            componentPointer: sourceContainerPointer,
            newContainerPointer: componentRef as CompRef
        })

        const {pageId, slotDataItem, slotsPointer} = getSlotInfoForComponent(
            createExtArgs,
            componentRef,
            innerElementPath
        )

        slotDataItem.slots[newSlotGuid] = duplicatedContainerRef.id
        dataModel.addItem(slotDataItem, DATA_TYPES.slots, pageId, slotsPointer!.id)
    }
}

const processDuplicateItem = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    newItem: PropertyItem,
    sourceItem: PropertyItem,
    arrayItemDataManifest: OverrideOrDefaultData
) => {
    const containerKeys = Object.keys(getManifestItems(arrayItemDataManifest.metaData)).filter(key =>
        shouldProcessContainerKey(arrayItemDataManifest, key)
    )

    containerKeys.forEach(key => {
        duplicateSystemContainer(createExtArgs, componentRef, sourceItem, newItem, key, arrayItemDataManifest)
    })
}

const processNewItem = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    newItem: PropertyItem,
    arrayItemDataManifest: OverrideOrDefaultData
) => {
    Object.keys(getManifestItems(arrayItemDataManifest.metaData)).forEach(key => {
        if (!shouldProcessContainerKey(arrayItemDataManifest, key)) {
            return
        }

        const slotName = guidUtils.getGUID()
        newItem[key] = slotName

        populateSlotWithSystemContainer(createExtArgs, componentRef, slotName, arrayItemDataManifest)
    })
}

const processRemovedItems = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    existingPropertyItems: PropertyItem[],
    updatedPropertyItems: PropertyItem[],
    arrayItemDataManifest: OverrideOrDefaultData
) => {
    const {extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const updatedItemIds = new Set(updatedPropertyItems.map(item => item._id).filter(Boolean))
    const removedItems = existingPropertyItems.filter(item => item._id && !updatedItemIds.has(item._id))

    removedItems.forEach(removedItem => {
        Object.keys(getManifestItems(arrayItemDataManifest.metaData)).forEach(key => {
            if (!shouldProcessContainerKey(arrayItemDataManifest, key)) {
                return
            }

            const innerElementPath = arrayItemDataManifest.path
            const slotGuid = removedItem[key]

            const {pageId, slotDataItem, slotsPointer} = getSlotInfoForComponent(
                createExtArgs,
                componentRef,
                innerElementPath
            )

            if (tryRemoveSystemContainerFromSlot(createExtArgs, slotDataItem, slotGuid)) {
                dataModel.addItem(slotDataItem, DATA_TYPES.slots, pageId, slotsPointer!.id)
            }
        })
    })
}

const processNewOrDuplicateItems = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    existingPropertyItems: PropertyItem[],
    updatedPropertyItems: PropertyItem[],
    arrayItemDataManifest: OverrideOrDefaultData
) => {
    const existingItemIds = new Set(existingPropertyItems.map(item => item._id).filter(Boolean))
    const newItems = updatedPropertyItems.filter(item => {
        if (!item._id) {
            return true
        }
        // A situation when new item is sent with id
        return !existingItemIds.has(item._id)
    })

    const containerKeys = Object.keys(getManifestItems(arrayItemDataManifest.metaData)).filter(key =>
        shouldProcessContainerKey(arrayItemDataManifest, key)
    )

    newItems.forEach(newItem => {
        const duplicateSource = findDuplicateSource(newItem, existingPropertyItems, containerKeys)

        if (duplicateSource) {
            processDuplicateItem(createExtArgs, componentRef, newItem, duplicateSource, arrayItemDataManifest)
        } else {
            processNewItem(createExtArgs, componentRef, newItem, arrayItemDataManifest)
        }
    })
}

const handleElementDataUpdate = (
    createExtArgs: CreateExtArgs,
    componentRef: CompRef,
    propertyItemMap: PropertyItemMap,
    arrayItemsListIncludeInnerElement: OverrideOrDefaultData[]
) => {
    const {extensionAPI} = createExtArgs
    const {data} = extensionAPI as DataExtensionAPI
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI

    arrayItemsListIncludeInnerElement.forEach((arrayItemDataManifest: OverrideOrDefaultData) => {
        const propertyItems = propertyItemMap[arrayItemDataManifest.prop]
        let existingPropertyItems

        if (arrayItemDataManifest.path) {
            existingPropertyItems =
                innerElements.getInnerElementData(componentRef, arrayItemDataManifest.path)?.[
                    arrayItemDataManifest.prop
                ] || []
        } else {
            existingPropertyItems = data.get(componentRef)?.[arrayItemDataManifest.prop] || []
        }

        processRemovedItems(createExtArgs, componentRef, existingPropertyItems, propertyItems, arrayItemDataManifest)

        processNewOrDuplicateItems(
            createExtArgs,
            componentRef,
            existingPropertyItems,
            propertyItems,
            arrayItemDataManifest
        )
    })
}

const validateNoDuplicateIds = (data: any) => {
    const seenIds = new Set<string>()
    const duplicateIds = new Set<string>()

    const collectIds = (items: any[]) => {
        items.forEach(item => {
            if (item._id) {
                if (seenIds.has(item._id)) {
                    duplicateIds.add(item._id)
                } else {
                    seenIds.add(item._id)
                }
            }
        })
    }

    Object.values(data).forEach(value => {
        if (Array.isArray(value)) {
            collectIds(value)
        }
    })

    if (duplicateIds.size > 0) {
        const duplicateIdsList = Array.from(duplicateIds).join(', ')
        throw new Error(`Duplicate id values found in update data: ${duplicateIdsList}`)
    }
}

export const builderComponentsDataUpdateHookOld = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    updatedDataItem: any
) => {
    const {extensionAPI} = createExtArgs
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI

    const componentType = components.getComponentType(componentRef)

    if (!schemaAPI.isSchemaCollectionRegistered(componentType)) {
        return
    }

    validateNoDuplicateIds(updatedDataItem)

    const arrayItemsListIncludeInnerElement = getDataToUpdate(manifest, componentRef, true, updatedDataItem)

    handleElementDataUpdate(createExtArgs, componentRef as CompRef, updatedDataItem, arrayItemsListIncludeInnerElement)
}
